/**
 * Map Repair Engine v1 — rollback.
 *
 * Czyta map_repair_log WHERE rollback_payload IS NOT NULL ORDER BY created_at DESC
 * i odtwarza stan sprzed repair: INSERT deleted nodes back, UPDATE relations z
 * rollback rewire, UPDATE seed status, etc.
 *
 * Advisory lock pattern jak w mapRepairEngine. Po rollback mark status='rolled_back'
 * i clear rollback_available_until. Re-audit.
 */

import { supabase } from "@/integrations/supabase/client";
import { auditMapFull } from "@/services/validation/mapAuditOrchestrator";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";
import type { RepairMapOptions, MapRepairLogRow } from "@/types/mapRepair";

const PREFIX = "[MAP-REPAIR-ROLLBACK]";

type LogCb = NonNullable<RepairMapOptions["onLog"]>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface RollbackResult {
  success: true;
  mapId: string;
  reverted: {
    R1_R2_nodes_restored: number;
    R1_R2_relations_rewired_back: number;
    R4_relations_restored: number;
    R7_seeds_restored: number;
    R3_moves_reverted: number;
    R6_links_removed: number;
  };
  duration_ms: number;
}

export async function rollbackRepair(
  mapId: string,
  opts: { onLog?: LogCb } = {},
): Promise<RollbackResult> {
  const t0 = Date.now();
  const onLog: LogCb = opts.onLog || (() => {});

  onLog("stage", `▶ Rollback start mapId=${mapId}`);
  logPublic(PREFIX, `rollback start mapId=${mapId}`);

  // Advisory lock
  try {
    const { data: lockRow, error: lockErr } = await db.rpc(
      "try_advisory_audit_lock",
      { p_map_id: mapId },
    );
    if (lockErr) {
      logDebug(PREFIX, `lock RPC error: ${lockErr.message}`);
    } else if (lockRow === false) {
      throw new Error("Rollback pominięty — inny audit/repair trwa");
    }
  } catch (e) {
    if ((e as Error).message.includes("pominięty")) throw e;
    logDebug(PREFIX, `lock error: ${(e as Error).message}`);
  }

  // Fetch wszystkie log entries z rollback_payload od ostatniego repair
  const { data: map } = await db
    .from("thematic_maps")
    .select("last_repaired_at")
    .eq("id", mapId)
    .maybeSingle();

  const lastRepairedAt = map?.last_repaired_at;
  if (!lastRepairedAt) {
    throw new Error("Brak ostatniego repair do rollback (last_repaired_at=NULL)");
  }

  const { data: logs, error: logsErr } = await db
    .from("map_repair_log")
    .select("*")
    .eq("map_id", mapId)
    .eq("dry_run", false)
    .not("rollback_payload", "is", null)
    .gte("created_at", lastRepairedAt)
    .order("created_at", { ascending: false });

  if (logsErr) throw logsErr;

  const entries = (logs || []) as MapRepairLogRow[];
  onLog("info", `Znaleziono ${entries.length} operacji do cofnięcia`);

  const counters = {
    R1_R2_nodes_restored: 0,
    R1_R2_relations_rewired_back: 0,
    R4_relations_restored: 0,
    R7_seeds_restored: 0,
    R3_moves_reverted: 0,
    R6_links_removed: 0,
  };

  // Reverse chronological — ostatnia zmiana najpierw
  for (const entry of entries) {
    const rb = entry.rollback_payload;
    if (!rb) continue;

    // R1/R2 merge rollback
    if (entry.operation === "merge" && (entry.rule_id === "R1" || entry.rule_id === "R2")) {
      const deletedNodes = (rb.deleted_nodes as Array<Record<string, unknown>>) || [];
      const rewiredRelations =
        (rb.rewired_relations as Array<Record<string, unknown>>) || [];
      const movedNode = rb.moved_node as {
        node_id: string;
        old_seed_id: string;
      } | null;

      // Restore deleted nodes (INSERT back)
      if (deletedNodes.length > 0) {
        await db.from("thematic_nodes").insert(deletedNodes);
        counters.R1_R2_nodes_restored += deletedNodes.length;
      }

      // Revert rewired relations to original from/to — upsert lub update per ID
      for (const r of rewiredRelations) {
        await db
          .from("thematic_node_relations")
          .upsert(r, { onConflict: "id" });
        counters.R1_R2_relations_rewired_back++;
      }

      // Revert R2 seed move
      if (movedNode) {
        await db
          .from("thematic_nodes")
          .update({ seed_id: movedNode.old_seed_id })
          .eq("id", movedNode.node_id);
      }
    }

    // R4 broken relations restore
    if (entry.operation === "delete" && entry.rule_id === "R4") {
      const deleted = (rb.deleted_relations as Array<Record<string, unknown>>) || [];
      if (deleted.length > 0) {
        await db.from("thematic_node_relations").insert(deleted);
        counters.R4_relations_restored += deleted.length;
      }
    }

    // R7 seed status revert
    if (entry.operation === "demote" && entry.rule_id === "R7") {
      await db
        .from("thematic_seeds")
        .update({ status: rb.old_status as string })
        .eq("id", rb.seed_id as string);
      counters.R7_seeds_restored++;
    }

    // R3 move revert
    if (entry.operation === "move" && entry.rule_id === "R3") {
      await db
        .from("thematic_nodes")
        .update({ seed_id: rb.old_seed_id as string })
        .eq("id", rb.node_id as string);
      counters.R3_moves_reverted++;
    }

    // R6 link remove (usunąć wstawiona relacja)
    if (entry.operation === "link" && entry.rule_id === "R6") {
      await db
        .from("thematic_node_relations")
        .delete()
        .eq("map_id", mapId)
        .eq("from_node_id", rb.from_node_id as string)
        .eq("to_node_id", rb.to_node_id as string);
      counters.R6_links_removed++;
    }
  }

  // Mark map
  await db
    .from("thematic_maps")
    .update({
      repair_status: "rolled_back",
      repair_rollback_available_until: null,
    })
    .eq("id", mapId);

  // Re-audit
  onLog("stage", "▶ Re-audit po rollback");
  try {
    await auditMapFull(mapId, { skipLLM: true });
  } catch (e) {
    logError(PREFIX, `re-audit po rollback failed: ${(e as Error).message}`);
  }

  const duration = Date.now() - t0;
  onLog(
    "success",
    `✓ Rollback done ${(duration / 1000).toFixed(1)}s · ` +
      `${counters.R1_R2_nodes_restored} nodów · ${counters.R1_R2_relations_rewired_back + counters.R4_relations_restored} relacji · ` +
      `${counters.R7_seeds_restored} seedów · R3=${counters.R3_moves_reverted} R6=${counters.R6_links_removed}`,
  );
  logPublic(PREFIX, `rollback done mapId=${mapId} ${duration}ms`);

  return {
    success: true,
    mapId,
    reverted: counters,
    duration_ms: duration,
  };
}

/**
 * Preview rollback — agregacja map_repair_log bez faktycznego rollback.
 * Używane w UI confirm dialog.
 */
export async function previewRollback(mapId: string): Promise<{
  deletedNodes: number;
  rewiredRelations: number;
  demotedSeeds: number;
  movedNodes: number;
  linkedRelations: number;
  cleanedRelations: number;
  available: boolean;
  executed_at: string | null;
  rollback_available_until: string | null;
}> {
  const { data: map } = await db
    .from("thematic_maps")
    .select(
      "last_repaired_at, repair_rollback_available_until, repair_status",
    )
    .eq("id", mapId)
    .maybeSingle();

  const available =
    !!map?.last_repaired_at &&
    !!map?.repair_rollback_available_until &&
    new Date(map.repair_rollback_available_until).getTime() > Date.now() &&
    map?.repair_status !== "rolled_back";

  if (!available) {
    return {
      deletedNodes: 0,
      rewiredRelations: 0,
      demotedSeeds: 0,
      movedNodes: 0,
      linkedRelations: 0,
      cleanedRelations: 0,
      available: false,
      executed_at: map?.last_repaired_at ?? null,
      rollback_available_until: map?.repair_rollback_available_until ?? null,
    };
  }

  const { data: logs } = await db
    .from("map_repair_log")
    .select("rule_id, operation, rollback_payload")
    .eq("map_id", mapId)
    .eq("dry_run", false)
    .not("rollback_payload", "is", null)
    .gte("created_at", map.last_repaired_at);

  const entries = (logs || []) as Array<Pick<MapRepairLogRow, "rule_id" | "operation" | "rollback_payload">>;

  let deletedNodes = 0;
  let rewiredRelations = 0;
  let demotedSeeds = 0;
  let movedNodes = 0;
  let linkedRelations = 0;
  let cleanedRelations = 0;

  for (const e of entries) {
    const rb = e.rollback_payload;
    if (!rb) continue;
    if (e.operation === "merge") {
      const dn = (rb.deleted_nodes as unknown[]) || [];
      deletedNodes += dn.length;
      const rr = (rb.rewired_relations as unknown[]) || [];
      rewiredRelations += rr.length;
      if (rb.moved_node) movedNodes++;
    }
    if (e.operation === "demote") demotedSeeds++;
    if (e.operation === "move") movedNodes++;
    if (e.operation === "link") linkedRelations++;
    if (e.operation === "delete" && e.rule_id === "R4") {
      const dr = (rb.deleted_relations as unknown[]) || [];
      cleanedRelations += dr.length;
    }
  }

  return {
    deletedNodes,
    rewiredRelations,
    demotedSeeds,
    movedNodes,
    linkedRelations,
    cleanedRelations,
    available: true,
    executed_at: map.last_repaired_at,
    rollback_available_until: map.repair_rollback_available_until,
  };
}
